import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, debounce } from '@mui/material';
import { getReservationList, markTripAsNoShow } from 'redux/actions/reservationAction';
import CommonSubHeader from 'components/commonSubHeader/CommonSubHeader';
import CommonTable from 'components/commonTable/CommonTable';
import CommonTableDialog from 'components/commonTableDialog/CommonTableDialog';
import AllotVehicleModal from '../allRides/AllotVehicleModal';
import CancelRegistrationModal from '../cancelRegistrationModal/CancelRegistrationModal';
import useModal from 'hooks/useModal';
import Loader from 'components/loader/Loader';
import { getDateFormat, handlePermissions, checkIsAdmin } from 'utils/utils';
import { roleAndPermissionEnum } from 'utils/rolesPermissionsEnum';
import NoShowModal from 'components/noShowModal/NoShowModal';

const Dispatch = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const startDate = searchParams?.get('startDate');
  const endDate = searchParams?.get('endDate');
  const filterPage = searchParams?.get('page');
  const filterRowPerPage = searchParams?.get('rowPerPage');
  const filterSearch = searchParams?.get('search');
  const reservationList = useSelector((state) => state.getReservationList?.reservationList?.data);

  const reservationListLoader = useSelector((state) => state.getReservationList?.loading);
  const { open: cancelModal, toggle: setCancelModal } = useModal();
  const { open: allotModal, toggle: setAllotModal } = useModal();
  const { open: noShowModal, toggle: setNoShowModal } = useModal();
  const [noShowTripDetails, setNoShowTripDetails] = useState(null);

  const [page, setPage] = useState(filterPage && !isNaN(filterPage) ? parseInt(filterPage) : 0);
  const [rowsPerPage, setRowsPerPage] = useState(filterRowPerPage ? filterRowPerPage : 10);
  const [search, setSearch] = useState(filterSearch ? filterSearch : '');
  const [details, setDetails] = useState({});
  const [dateFrom, setDateFrom] = useState(
    startDate && startDate !== 'null'
      ? startDate
      : startDate === 'null'
        ? ''
        : getDateFormat(new Date(), 'reservationDateFormat'),
  );
  const [dateTo, setDateTo] = useState(
    endDate && endDate !== 'null'
      ? endDate
      : endDate === 'null'
        ? ''
        : getDateFormat(new Date(), 'reservationDateFormat'),
  );

  let paramsList = {};
  searchParams.forEach((value, key) => {
    paramsList[key] = value;
  });

  let filterData = {
    B2B: true,
    page: page,
    dateFrom: dateFrom ? dateFrom : '',
    dateTo: dateTo ? dateTo : '',
    status: 'dispatched',
    pageSize: rowsPerPage ? rowsPerPage : '',
    search: search ? search : '',
  };

  useEffect(() => {
    dispatch(getReservationList(filterData));
  }, []);

  const menuData = [
    {
      id: '1',
      label: 'Print Duty slip',
    },
    {
      id: '2',
      label: 'Re-Allot',
    },
    {
      id: '3',
      label: 'Cancel Duty',
    },
  ];

  const [open, setOpen] = useState(null);

  const handleDialog = (event) => {
    setOpen(event);
  };

  const tableOnClickRow = (navigate, val) => {
    navigate(
      `/operation/dispatch/bookingdetails?id=${val.id}&startDate=${dateFrom}&endDate=${dateTo}&type=dispatch&page=${page}&rowPerPage=${rowsPerPage}&search=${search}`,
    );
  };

  const onSearchQuery = debounce((e) => {
    setSearchParams({ ...paramsList, page: 0, search: e });
    setPage(0);
    setSearch(e);
    const searchFilterData = {
      ...filterData,
      search: e,
      page: 0,
      pageSize: '',
      status: 'dispatched',
    };
    dispatch(getReservationList(searchFilterData));
  }, 1000);

  const onSelectDateRange = (newValue) => {
    if (newValue === null || newValue === 0) {
      setSearchParams({ ...paramsList, startDate: 'null', endDate: 'null' });
      setDateFrom('');
      setDateTo('');
      filterData = { ...filterData, dateFrom: '', dateTo: '' };
    } else {
      setSearchParams({
        ...paramsList,
        startDate: getDateFormat(newValue[0], 'reservationDateFormat'),
        endDate: getDateFormat(newValue[1], 'reservationDateFormat'),
      });
      setDateFrom(getDateFormat(newValue[0], 'reservationDateFormat'));
      setDateTo(getDateFormat(newValue[1], 'reservationDateFormat'));
      filterData = {
        ...filterData,
        dateFrom: getDateFormat(newValue[0], 'reservationDateFormat'),
        dateTo: getDateFormat(newValue[1], 'reservationDateFormat'),
      };
    }
    dispatch(getReservationList(filterData));
  };

  const handleChangePage = (event, newPage) => {
    setSearchParams({ ...paramsList, page: newPage });
    setPage(newPage);
    dispatch(
      getReservationList({
        ...filterData,
        page: newPage,
        status: 'dispatched',
      }),
    );
  };

  const handleChangeRowsPerPage = (event) => {
    setSearchParams({
      ...paramsList,
      rowPerPage: parseInt(event.target.value),
      page: 0,
    });
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    dispatch(
      getReservationList({
        ...filterData,
        page: 0,
        pageSize: parseInt(event.target.value),
        status: 'dispatched',
      }),
    );
  };

  const onClickMoreIcon = (tripData, ele) => {
    if (ele === 'Re-Allot') {
      setDetails(tripData);
      setAllotModal();
    } else if (ele === 'Cancel Duty') {
      setDetails(tripData);
      setCancelModal();
    }
  };

  const onMarkNoShow = (tripData) => {
    setNoShowTripDetails(tripData);
    setNoShowModal();
  };

  const handleNoShowSubmit = (noShowData) => {
    dispatch(markTripAsNoShow(noShowData, 'listPage'));
    setNoShowModal();
    setNoShowTripDetails(null);
  };

  const role = localStorage.getItem('role');
  const isAdmin = checkIsAdmin();

  const roleAndPermissions = useSelector(
    (state) => state?.getRolesAndPermissions?.rolesAndPermissions,
  );
  const dispatchRoles = roleAndPermissionEnum.operation;
  const accessForCancel = handlePermissions(roleAndPermissions, dispatchRoles.moduleKey, {
    customAccess: dispatchRoles.dispatch.dispatchCancel,
  });
  const hasReallotPermission = handlePermissions(roleAndPermissions, dispatchRoles.moduleKey, {
    customAccess: dispatchRoles.allotTrip.allotTripReallot,
  });
  const accessForReallot = isAdmin
    ? hasReallotPermission
    : hasReallotPermission ||
      handlePermissions(roleAndPermissions, dispatchRoles.moduleKey, {
        customAccess: dispatchRoles.allRides.allRidesAllot,
      });

  const hasViewMoreIcon = accessForCancel || accessForReallot;
  const accessForAllotAndCancel = accessForCancel && accessForReallot;

  return (
    <Box>
      <Box className="mb20">
        <CommonSubHeader
          filter={false}
          buttonName={''}
          onSelectDateRange={onSelectDateRange}
          onSearchQuery={onSearchQuery}
          dateFrom={dateFrom}
          dateTo={dateTo}
          search={search}
        />
      </Box>
      {reservationListLoader ? (
        <Loader />
      ) : (
        <CommonTable
          isClickable
          data={reservationList}
          tableOnClickRow={tableOnClickRow}
          handleDialog={handleDialog}
          page={page}
          accessForCancel={accessForCancel}
          accessForReallot={accessForReallot}
          accessForAllotAndCancel={accessForAllotAndCancel}
          rowsPerPage={rowsPerPage}
          onSearchQuery={onSearchQuery}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          tabName="operation"
          onClickMoreIcon={onClickMoreIcon}
          viewMoreIcon={!hasViewMoreIcon}
          enableTripHighlight={true}
          showDispatchLegend={true}
          onMarkNoShow={accessForCancel || isAdmin ? onMarkNoShow : undefined}
        />
      )}
      <CommonTableDialog menuData={menuData} open={open} setOpen={setOpen} />
      {allotModal && (
        <AllotVehicleModal
          open={allotModal}
          toggle={setAllotModal}
          tripDetails={details}
          page={'dispatch'}
          payload={filterData}
        />
      )}
      {cancelModal && (
        <CancelRegistrationModal
          page={'dispatch'}
          open={cancelModal}
          toggle={setCancelModal}
          reservationData={details}
          payload={filterData}
        />
      )}
      {noShowModal && (
        <NoShowModal
          open={noShowModal}
          toggle={setNoShowModal}
          tripDetails={noShowTripDetails}
          onSubmit={handleNoShowSubmit}
        />
      )}
    </Box>
  );
};

export default Dispatch;
